import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { site, nav } from '../content.js'
import Icon from '../components/Icon.jsx'
import Logo from '../components/Logo.jsx'
import { useSignedIn, useMyVenue, signOut } from '../components/SignIn.jsx'

// The signed-in venue's menu, like listaa's: dashboard, edit venue, account settings, sign out.
// Closes on outside click, Escape, or picking an item.
function AccountMenu({ icon, name }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])
  return (
    <div className="account-menu" ref={ref}>
      <button type="button" className="btn btn-outline btn-sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {icon} {name} <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="account-pop" role="menu" onClick={() => setOpen(false)}>
          <div className="account-who">{icon}<div><strong>{name}</strong><span>Venue Owner</span></div></div>
          <a role="menuitem" href={site.accountUrl}><Icon name="grid" size={16} /> Dashboard</a>
          <a role="menuitem" href={site.editVenueUrl}><Icon name="edit" size={16} /> Edit Venue</a>
          <a role="menuitem" href={site.accountSettingsUrl}><Icon name="settings" size={16} /> Account Settings</a>
          <hr />
          <button role="menuitem" type="button" onClick={signOut}><Icon name="logout" size={16} /> Sign Out</button>
        </div>
      )}
    </div>
  )
}

// The section nearest the top of the screen, so the menu can highlight where you are.
function useActiveSection(ids, enabled) {
  const [active, setActive] = useState(null)
  useEffect(() => {
    if (!enabled) return
    const onScroll = () => {
      // Go by where each section sits on the page, not by menu order, so reordering sections is safe.
      const els = ids.map((id) => document.getElementById(id)).filter(Boolean)
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
      let cur = null
      for (const el of els) if (el.getBoundingClientRect().top <= 140) cur = el.id
      // At the very bottom the last sections can't reach the top, so count the lowest one as reached.
      if (els.length && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) cur = els[els.length - 1].id
      setActive(cur)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [ids, enabled])
  return active
}
const navIds = nav.filter((n) => n.href.startsWith('#')).map((n) => n.href.slice(1))

// linkBase prefixes the section links ('/' when rendered outside the home page).
export default function Header({ linkBase = '' }) {
  const [open, setOpen] = useState(false)
  const section = useActiveSection(navIds, !linkBase)
  const { pathname } = useLocation()
  // Section links light up by scroll position, page links by the current route. '/pricing#packages'
  // is the Packages page itself (it opens at that section), while '/pricing#faq' is only a jump into it.
  const isActive = (href) => href.startsWith('#') ? section === href.slice(1) : pathname === href.replace(/#packages$/, '')
  const signedIn = useSignedIn()
  const venue = useMyVenue(signedIn)
  const venueLoading = signedIn && venue === undefined
  const venueName = venue?.name || 'My Venue'
  const venueIcon = venue?.logo
    ? <img src={venue.logo} alt="" className="venue-logo" />
    : <Icon name="user" size={16} />
  return (
    <header className="header">
      <div className="container header-inner">
        <Logo linkBase={linkBase} />
        <nav className={`nav ${open ? 'open' : ''}`} onClick={() => setOpen(false)}>
          {nav.filter((n) => !n.footerOnly).map((n) => <a key={n.href} href={n.href.startsWith('#') ? linkBase + n.href : n.href}className={isActive(n.href) ? 'active' : undefined} aria-current={isActive(n.href) ? (n.href.startsWith('#') ? 'true' : 'page') : undefined}>{n.label}</a>)}
          {/* The header's Sign In button is hidden on small screens, so the menu carries it there. */}
          {signedIn
            ? <>
                <a href={site.accountUrl} className="nav-account">{venueName}</a>
                <a href={site.editVenueUrl} className="nav-account nav-sub">Edit Venue</a>
                <a href={site.accountSettingsUrl} className="nav-account nav-sub">Account Settings</a>
                <button type="button" className="nav-account nav-logout" onClick={signOut}>Log out</button>
              </>
            : <a href={site.loginUrl} className="nav-account">Sign In</a>}
        </nav>
        <div className="header-actions">
          {!signedIn && <a href={site.signupUrl} className="btn btn-pink btn-sm"><Icon name="plus" size={16} /> Add Venue</a>}
          {signedIn ? (
            <>
              {venueLoading
                ? <span className="btn btn-outline btn-sm venue-skeleton" aria-label="Loading your venue"><i /><b /></span>
                : <AccountMenu icon={venueIcon} name={venueName} />}
            </>
          ) : (
            <a href={site.loginUrl} className="btn btn-outline btn-sm">
              <Icon name="user" size={16} /> Sign In
            </a>
          )}
          <button className="menu-btn" aria-label="Menu" onClick={() => setOpen(!open)}>
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
  )
}
